import { ActorType, EventName, EventType, metrics, Status } from '@prophecy/metrics';

/**
 * Editor-open milestones, each emitted on its own the moment it happens — no shared handle, no
 * offsets, no ordering. `context.socketId` joins everything from `connect` onwards (the editor
 * WebSocket session id, minted on initialize and stamped on every event by `MetricsProvider`'s
 * `getIds`), and it is what edweb's `ide.session.*` stages carry too. `open_click` and
 * `metadata_loaded` predate that id, so they attach by being nearest in time; durations come from
 * differencing event timestamps, not from anything the browser computes.
 *
 * A phase is any `ui.session.*` the spec defines, less the three with their own emitter below —
 * `open_click` is a user action, `did_open` is named by its notification, and `compile_failed` is
 * the failure leg of `trackLoadCompile`.
 */
type LoadEntity = { entityType?: 'pipeline' | 'sqlProject' | 'job'; entityId?: string; entityName?: string };

/**
 * Is a load in progress? Only `did_open` needs this: its notifications keep arriving for the life
 * of the socket, so without a window it reports every gem dialog and every file open as a load
 * milestone. The window opens at `connect` (the first phase the socket can produce) and closes at
 * `project_usable` — plus a hard cap, so a load that never reaches that phase (a snapshot or
 * read-only open runs no load compile) cannot leave it open for the rest of the session.
 */
const LOAD_WINDOW_CAP_MS = 2 * 60 * 1000;
let loadWindowOpenedAt: number | undefined;

const isLoadInFlight = () => {
  if (loadWindowOpenedAt === undefined) return false;
  if (performance.now() - loadWindowOpenedAt <= LOAD_WINDOW_CAP_MS) return true;
  loadWindowOpenedAt = undefined;
  return false;
};

export function markLoadPhase(
  phase: Exclude<keyof typeof EventName.ui.session, 'open_click' | 'did_open' | 'compile_failed'>,
  entity: LoadEntity & { status?: Status } = {}
): void {
  // `connect` is the first phase the socket can produce and `project_usable` the terminal one, so
  // the pair bounds the window `markDidOpen` checks.
  if (phase === 'connect') loadWindowOpenedAt = performance.now();
  else if (phase === 'project_usable') loadWindowOpenedAt = undefined;
  metrics.track({
    eventName: EventName.ui.session[phase],
    eventType: EventType.system,
    actorType: ActorType.system,
    payload: entity
  });
}

/** Test seam: the load window is module state, so it has to be cleared between cases. */
export function resetSessionLoadTracking(): void {
  loadWindowOpenedAt = undefined;
}

/**
 * The load compile, as a pair: `compile_finish` on resolve, `compile_failed` on reject. Without the
 * failure leg a compile that never finished and one that finished badly look identical — the phase
 * is simply absent from both funnels.
 */
export function trackLoadCompile(compile: Promise<unknown>, entity: LoadEntity): Promise<unknown> {
  return compile.then(
    (result) => {
      markLoadPhase('compile_finish', { ...entity, status: Status.success });
      return result;
    },
    (error) => {
      metrics.track({
        eventName: EventName.ui.session.compile_failed,
        eventType: EventType.system,
        actorType: ActorType.system,
        payload: { ...entity, status: Status.failure }
      });
      throw error;
    }
  );
}

/** The anchor: every project open starts here, including opens that die before a socket exists. */
export function markOpenClick(entity: LoadEntity): void {
  metrics.track({
    eventName: EventName.ui.session.open_click,
    eventType: EventType.userAction,
    actorType: ActorType.user,
    payload: entity
  });
}

/**
 * One per `*\/didOpen` frame the editor socket delivers during a load, named by the notification
 * that carried it. Gated on the load window: the same notifications carry ordinary interactions
 * once the editor is up (`properties/didOpen` on every gem dialog, `textDocuments/didOpen` on every
 * file open), and those are not load milestones.
 */
export function markDidOpen(notification: string): void {
  if (!isLoadInFlight()) return;
  metrics.track({
    eventName: EventName.ui.session.did_open,
    eventType: EventType.system,
    actorType: ActorType.system,
    payload: { notification }
  });
}
